import { Head, Link } from '@inertiajs/react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Calendar, 
  User, 
  ArrowLeft, 
  BookOpen, 
  Heart,
  MessageCircle,
  Facebook,
  Twitter,
  Linkedin
} from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  content: string;
  author: string;
  published_at: string;
}

interface SimilarPost {
  id: number;
  title: string;
  published_at: string;
}

interface Props {
  blog_post: BlogPost;
  similar_posts?: SimilarPost[];
}

export default function BlogShow({ blog_post, similar_posts = [] }: Props) {
  return (
    <>
      <Head title={`${blog_post.title} - Blog Ethos Santé`} />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <Link href="/blog">
              <Button variant="ghost" size="sm" className="mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au blog
              </Button>
            </Link>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <Badge variant="secondary">
                  <Calendar className="w-3 h-3 mr-1" />
                  {format(new Date(blog_post.published_at), 'd MMMM yyyy', { locale: fr })}
                </Badge>
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  <span>{blog_post.author}</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                {blog_post.title}
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Découvrez cet article sur la santé et le bien-être, rédigé par notre équipe d'experts.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-none">
                <CardContent className="p-0">
                  <div className="prose prose-lg max-w-none">
                    <div className="whitespace-pre-wrap text-gray-900 leading-relaxed text-lg">
                      {blog_post.content}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Article Actions */}
              <div className="mt-12 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button variant="outline" size="sm">
                    <Heart className="w-4 h-4 mr-2" />
                    J'aime
                  </Button>
                  <Button variant="outline" size="sm">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Commenter
                  </Button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Partager :</span>
                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                    <Facebook className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-500">
                    <Twitter className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-800">
                    <Linkedin className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                {/* Author Info */}
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold">À propos de l'auteur</h3>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{blog_post.author}</p>
                        <p className="text-sm text-gray-600">Équipe Ethos Santé</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Related Articles */}
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold">Articles similaires</h3>
                  </CardHeader>
                  <CardContent>
                    {similar_posts.length > 0 ? (
                      <div className="space-y-3">
                        {similar_posts.map((post) => (
                          <Link 
                            key={post.id} 
                            href={`/blog/${post.id}`}
                            className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                              {post.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {format(new Date(post.published_at), 'd MMMM yyyy', { locale: fr })}
                            </p>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Aucun article similaire disponible</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-gray-50 border-t">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <BookOpen className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Restez informé de nos derniers articles
            </h2>
            <p className="text-gray-600 mb-6">
              Recevez nos articles sur la santé et le bien-être directement dans votre boîte mail
            </p>
            <div className="flex max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <Button className="rounded-l-none">
                S'abonner
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 
